import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Loader2, Clock, Camera, ShieldCheck, Droplets, Wind,
  Users, Waves, Caravan, Navigation, ExternalLink,
} from 'lucide-react';
import { getFacility } from '../lib/api';
import { useSearchStore } from '../store/useSearchStore';
import PhotoGallery from '../components/listing/PhotoGallery';
import UnitRow from '../components/facility/UnitRow';
import QuotePanel from '../components/facility/QuotePanel';
import VehicleProfile from '../components/facility/VehicleProfile';
import { formatCents, formatDate, formatFeet, gateHours } from '../lib/format';
import { CheckCircle2, KeyRound } from 'lucide-react';
import { useSeo, useJsonLd, selfStorageSchema } from '../lib/seo';
import { directionsUrl, externalLink } from '../lib/maps';

/**
 * Facility detail. Reading order is deliberate: what fits → photos → the units
 * themselves → cost. The quote panel sticks on desktop so the total is never
 * scrolled away while comparing units.
 */
export default function FacilityDetailPage() {
  const { slug } = useParams();
  const vehicle = useSearchStore((s) => s.vehicle);

  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [showRented, setShowRented] = useState(false);

  const seoFacility = data?.facility ?? null;
  useSeo({
    title: seoFacility ? `${seoFacility.name} — ${seoFacility.city}, ${seoFacility.state_code}` : undefined,
    description: seoFacility
      ? `${seoFacility.name} in ${seoFacility.city}, ${seoFacility.state_code}. `
        + `Monthly RV, trailer and boat storage with sizes and prices listed. `
        + `Check whether your rig fits before you drive over.`
      : undefined,
    path: seoFacility ? `/facilities/${seoFacility.slug}` : undefined,
  });
  useJsonLd('facility', useMemo(() => selfStorageSchema(seoFacility), [seoFacility]));

  useEffect(() => {
    let cancelled = false;
    setState('loading');

    getFacility(slug, {
      vehicle_length: vehicle.length,
      vehicle_width: vehicle.width,
      vehicle_height: vehicle.height,
    })
      .then((payload) => {
        if (cancelled) return;
        setData(payload);
        setState('ready');
        // Re-grading after a vehicle change can invalidate the current pick.
        setSelectedUnit((current) => {
          if (!current) return null;
          const fresh = payload.units.find((u) => u.public_id === current.public_id);
          return fresh && fresh.status === 'available' && fresh.fit.status !== 'incompatible'
            ? fresh : null;
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setState('error');
      });

    return () => { cancelled = true; };
  }, [slug, vehicle.length, vehicle.width, vehicle.height]);

  if (state === 'loading') {
    return (
      <div className="grid h-full place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" />
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="grid h-full place-items-center p-8 text-center">
        <div>
          <p className="text-sm font-bold text-slate-900">{error}</p>
          <Link to="/search" className="btn-ghost mt-4">Back to search</Link>
        </div>
      </div>
    );
  }

  const { facility, units, summary } = data;

  const availableUnits = units.filter((u) => u.status === 'available');
  const rentedUnits = units.filter((u) => u.status !== 'available');
  // Everything, but only when there is nothing to rent — see the note by the
  // toggle further down.
  const shownUnits = availableUnits.length === 0 || showRented ? units : availableUnits;

  if (confirmation) return <ReservationConfirmed result={confirmation} />;

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-3 sm:px-6">
          <Link to="/search" className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> <span className="hidden sm:inline">Back</span>
          </Link>
          <p className="min-w-0 flex-1 truncate text-sm font-bold text-slate-900">{facility.name}</p>
          <VehicleProfile />
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{facility.name}</h1>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <p className="flex items-center gap-1.5 text-sm text-slate-600">
            <MapPin className="h-4 w-4 text-brand-500" aria-hidden="true" />
            {facility.address_line1}, {facility.city}, {facility.state_code} {facility.postal_code}
          </p>
          {directionsUrl(facility.lat, facility.lng) && (
            <a href={directionsUrl(facility.lat, facility.lng, facility.name)} {...externalLink}
               className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-bold text-slate-700 hover:bg-slate-50">
              <Navigation className="h-3.5 w-3.5" aria-hidden="true" />
              Directions
              <ExternalLink className="h-3 w-3 text-brand-500" aria-hidden="true" />
              <span className="sr-only">(opens in a new tab)</span>
            </a>
          )}
        </div>

        <div className="mt-4">
          <PhotoGallery photos={facility.photos} title={facility.name} />
        </div>

        {/* Answers the only question that matters before price. */}
        <FitSummary summary={summary} vehicle={vehicle} />

        <div className="mt-6 grid gap-8 lg:grid-cols-3">
          <div className="space-y-8 lg:col-span-2">
            <section>
              <h2 className="text-lg font-extrabold text-slate-900">About this facility</h2>
              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-700">
                {facility.description}
              </p>
              <FacilityFeatures facility={facility} />
            </section>

            <section>
              <h2 className="text-lg font-extrabold text-slate-900">
                Available spaces
                <span className="ml-2 text-sm font-medium text-slate-500">
                  {summary.fitting_available_units} of {availableUnits.length} fit your vehicle
                </span>
              </h2>
              <ul className="mt-4 space-y-3">
                {shownUnits.map((unit) => (
                  <UnitRow key={unit.public_id} unit={unit}
                           isSelected={selectedUnit?.public_id === unit.public_id}
                           onSelect={setSelectedUnit} />
                ))}
              </ul>

              {/*
                A rented space is worth showing when it is the only thing here —
                a bare page reads as "this business has nothing", where a greyed
                row reads as "full, check back". Once anything is rentable that
                stops being true and they are purely rows to scroll past, so
                they collapse behind a count the visitor can open if they want
                to size up the yard.
              */}
              {rentedUnits.length > 0 && availableUnits.length > 0 && (
                <button type="button" onClick={() => setShowRented(!showRented)}
                        aria-expanded={showRented}
                        className="mt-4 text-sm font-bold text-brand-700 hover:underline">
                  {showRented
                    ? 'Hide spaces that are already rented'
                    : `Show ${rentedUnits.length} space${rentedUnits.length === 1 ? '' : 's'} that ${rentedUnits.length === 1 ? 'is' : 'are'} already rented`}
                </button>
              )}
            </section>
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-20">
              <QuotePanel unit={selectedUnit} facility={facility} onReserved={setConfirmation} />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function FitSummary({ summary, vehicle }) {
  const none = summary.fitting_available_units === 0;
  return (
    <section className={`mt-4 rounded-xl border p-4 ${
      none ? 'border-rose-200 bg-rose-50 text-rose-900' : 'border-emerald-200 bg-emerald-50 text-emerald-900'}`}>
      <div className="flex items-start gap-3">
        <Caravan className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <div>
          <p className="text-sm font-extrabold">
            {none
              ? `Nothing here fits a ${formatFeet(vehicle.length)} vehicle right now`
              : `${summary.fitting_available_units} space${summary.fitting_available_units === 1 ? '' : 's'} fit your ${formatFeet(vehicle.length)} vehicle`}
          </p>
          <p className="mt-0.5 text-xs">
            {none
              ? 'Units below are shown anyway so you can see how close they come.'
              : `From $${(summary.cheapest_fitting_cents / 100).toFixed(0)}/month. Units that don't fit are marked in red.`}
          </p>
        </div>
      </div>
    </section>
  );
}

function FacilityFeatures({ facility }) {
  const features = [
    { on: true, Icon: Clock, label: gateHours(facility) },
    { on: facility.has_cameras, Icon: Camera, label: '24/7 camera coverage' },
    { on: facility.has_perimeter_fence, Icon: ShieldCheck, label: 'Perimeter fencing' },
    { on: facility.has_onsite_staff, Icon: Users, label: 'On-site staff' },
    { on: facility.has_dump_station, Icon: Droplets, label: 'Dump station' },
    { on: facility.has_wash_bay, Icon: Waves, label: 'Wash bay' },
    { on: facility.has_air_compressor, Icon: Wind, label: 'Air compressor' },
    { on: facility.has_potable_water, Icon: Droplets, label: 'Potable water' },
  ].filter((f) => f.on);

  return (
    <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
      {features.map(({ Icon, label }) => (
        <div key={label} className="rounded-lg border border-slate-200 bg-white p-3">
          <Icon className="h-4 w-4 text-brand-600" aria-hidden="true" />
          <dd className="mt-1.5 text-xs font-bold text-slate-900">{label}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The QR token is returned exactly once by the reservation endpoint and is
 * never retrievable again, so this screen has to make that explicit rather
 * than let a tenant assume they can come back for it.
 */
function ReservationConfirmed({ result }) {
  const { lease, unit, facility, invoice, access } = result;
  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="mx-auto max-w-lg px-4 py-12">
        <div className="rounded-2xl border border-emerald-200 bg-white p-6 shadow-card">
          <CheckCircle2 className="h-10 w-10 text-emerald-600" aria-hidden="true" />
          <h1 className="mt-3 text-2xl font-extrabold tracking-tight text-slate-900">
            Unit {unit.unit_number} is yours
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {facility.name} · {facility.city}, {facility.state_code}
          </p>

          <dl className="mt-5 space-y-2 border-t border-slate-200 pt-5 text-sm">
            <Row label="Lease" value={lease.lease_number} />
            <Row label="Move-in" value={formatDate(lease.starts_on)} />
            <Row label="Monthly rate" value={`${formatCents(lease.monthly_rate_cents)}/mo`} />
            <Row label="First invoice" value={`${invoice.invoice_number} — ${formatCents(invoice.total_cents)}`} />
          </dl>

          <div className="mt-5 rounded-xl border border-slate-800 bg-slate-900 p-4 text-slate-100">
            <p className="flex items-center gap-2 text-sm font-bold">
              <KeyRound className="h-4 w-4 text-amber-400" aria-hidden="true" /> Gate access
            </p>
            <p className="mt-3 text-[12px] font-semibold uppercase tracking-wide text-slate-400">Gate code</p>
            <code className="mt-1 block rounded bg-slate-800 px-3 py-2 font-mono text-lg font-bold tracking-widest">
              {access.gate_code}
            </code>
            <p className="mt-3 text-[12px] font-semibold uppercase tracking-wide text-slate-400">
              QR token — shown once
            </p>
            <code className="mt-1 block break-all rounded bg-slate-800 px-3 py-2 font-mono text-[12px] text-brand-300">
              {access.qr_token}
            </code>
            <p className="mt-2 text-[12px] text-slate-400">
              Save this now. We store only a hash of it, so we cannot show it to you again —
              you would need a new credential issued.
            </p>
          </div>

          <div className="mt-5 flex gap-3">
            <Link to="/account/spaces" className="btn-primary flex-1">View my spaces</Link>
            <Link to="/search" className="btn-ghost flex-1">Keep browsing</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-semibold text-slate-900">{value}</dd>
    </div>
  );
}
